import { useEffect, useRef, useState } from "react";
import{ word } from "shared/types/Report";
import useView from "./useView";
import useFirestore from "shared/hooks/useFirestore";
import useStorage from "shared/hooks/useStorage";

const useWordView = (list:word[], setList:React.Dispatch<React.SetStateAction<word[]>>, path:string) => {
    const {uploadStorage, deleteStorage} = useStorage();
    const baseHook = useView(list, setList, path);
    const imageInputRefs = useRef<Array<HTMLInputElement | null>>([]);
    const wordAudioInputRefs = useRef<Array<HTMLInputElement | null>>([]);
    const exampleAudioInputRefs = useRef<Array<HTMLInputElement | null>>([]);
      
    const onAddClickHandler = () => {
        setList(prev => [...prev, {
            id:prev.length ? prev[prev.length -1].id + 1 : 1,
            word:"",
            kor:"",
            example:"",
            example_kor:"",
            image:null,
            word_audio:"",
            example_audio:""
        }])
    }

    const onImageInputClickHandler = (index: number) => {
        const inputElement = imageInputRefs.current[index];
        if (inputElement) {
          inputElement.click();
        } else {
          console.error(`Input element at index ${index} is null`);
        }
      };

    const onImageChangeHandler = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                updateItemImage(index, reader.result as string, file);
            };
            reader.readAsDataURL(file);
        }
    };

    const updateItemImage = (index: number, imageUrl: string, file:File) => {
        const newList = list.map((item, idx) => {
            if (idx === index) {
            return { ...item, image_preview: imageUrl, image:file};
            }
            return item;
        });
        setList(newList);
    };

    const onWordAudioInputClickHandler = (index: number) => {
        const inputElement = wordAudioInputRefs.current[index];
        if(inputElement){
            inputElement.click()
        }else{
            console.error(`Input element at index ${index} is null`);
        }
    }

    const onExampleAudioInputClickHandler = (index: number) => {
        const inputElement = exampleAudioInputRefs.current[index];
        if(inputElement){
            inputElement.click()
        }else{
            console.error(`Input element at index ${index} is null`);
        }
    }

    const onWordAudioChangeHandler = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if(file){
            const newList = list.map((item, idx) => {
                const fileURL = URL.createObjectURL(file);
                if (idx === index) {
                return { ...item, word_audio:fileURL};
                }
                return item;
            });
            setList(newList);
        }
    }

    const onExampleAudioChangeHandler = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if(file){
            const newList = list.map((item, idx) => {
                const fileURL = URL.createObjectURL(file);
                if (idx === index) {
                return { ...item, example_audio:fileURL};
                }
                return item;
            });
            setList(newList);
        }
    }

    const onWordAudioDeleteHandler = (index: number) => {
        const newList = list.map((item, idx) => {
            if (idx === index) {
                return { ...item, word_audio: "" };
            }
            return item;
        });
        setList(newList);
    }

    const onExampleAudioDeleteHandler = (index: number) => {
        const newList = list.map((item, idx) => {
            if (idx === index) {
                return { ...item, example_audio: "" };
            }
            return item;
        });
        setList(newList);
    }

    const onRegisterReportClickHandler = async() => {
        const data = await registerImage();
        const audio = await registerAudio(data);
        const register_data = baseHook.removeKeyFromObjects(audio, "image_preview");
        baseHook.onRegisterReportClick("word", register_data);
    }

    const registerImage = async() => {
        let wordList = list;
        let prevImageUrl = null;
        baseHook.setShowProgress(true);
        for(let i = 0; i < list.length; i++){
            const image = list[i].image;
            if (image instanceof File) {
                baseHook.setUploadFileName(image.name);
                const image_url = await uploadStorage(path + "/word", image, baseHook.setProgress).then(res => {
                    return res || "";
                })
                wordList[i].image = image_url;
                prevImageUrl = image_url;
            }
            if(!image && prevImageUrl){
                wordList[i].image = prevImageUrl;
            }
        }
        baseHook.setShowProgress(false);
        return wordList;
    }

    const registerAudio = async (list:word[]) => {
        let wordList = list;
        baseHook.setShowProgress(true);
      
        for (let i = 0; i < list.length; i++) {
          const wordAudio = list[i].word_audio;
          const exampleAudio = list[i].example_audio;
      
          if (wordAudio) {
            let file;
            try {
              if (typeof wordAudio === "string") {
                const response = await fetch(wordAudio);
                const blob = await response.blob();
                file = new File([blob], `word-audio-${i}.mp3`, {
                  type: blob.type,
                  lastModified: Date.now(),
                });
              }
              baseHook.setUploadFileName(`word-audio-${i}.mp3`);
              if(file){
                const audio_url = await uploadStorage(
                    path + "/word",
                    file,
                    baseHook.setProgress
                  ).then((res) => {
                    return res || "";
                  });
                  wordList[i].word_audio = audio_url;
              }
            } catch (error) {
              console.error("단어 오디오 처리 중 오류 발생:", error);
            }
          }

          if (exampleAudio) {
            let file;
            try {
              if (typeof exampleAudio === "string") {
                const response = await fetch(exampleAudio);
                const blob = await response.blob();
                file = new File([blob], `example-audio-${i}.mp3`, {
                  type: blob.type,
                  lastModified: Date.now(),
                });
              }
              baseHook.setUploadFileName(`example-audio-${i}.mp3`);
              if(file){
                const audio_url = await uploadStorage(
                    path + "/word",
                    file,
                    baseHook.setProgress
                  ).then((res) => {
                    return res || "";
                  });
                  wordList[i].example_audio = audio_url;
              }
            } catch (error) {
              console.error("예문 오디오 처리 중 오류 발생:", error);
            }
          }
        }
      
        baseHook.setShowProgress(false);
        return wordList;
      };

    const onDeleteImageClickHandler = () => {
        const newList = list.map((item, idx) => {
            if (idx === baseHook.selectedFile.index) {
            return { ...item, image_preview:"", image:null};
            }
            return item;
        });
        if(baseHook.selectedFile.url.includes("https")){
            deleteStorage(path + "/word", baseHook.selectedFile.url);
            const register_data = baseHook.removeKeyFromObjects(newList, "image_preview");
            baseHook.onRegisterReportClick("word", register_data);
        }
        setList(newList);
    }

    return {
        ...baseHook, 
        onAddClickHandler, 
        imageInputRefs, 
        wordAudioInputRefs,
        exampleAudioInputRefs,
        onImageInputClickHandler, 
        onImageChangeHandler, 
        onRegisterReportClickHandler, 
        onDeleteImageClickHandler,
        onWordAudioInputClickHandler,
        onExampleAudioInputClickHandler,
        onWordAudioChangeHandler,
        onExampleAudioChangeHandler,
        onWordAudioDeleteHandler,
        onExampleAudioDeleteHandler
    }
}

export default useWordView;